import Cookies from 'js-cookie';
import { reportLog } from '@/api/log';
import type { LogReportData, LogEventExt } from '@/types/log';

/**
 * Device ID 存储 key（cookie 与 localStorage 同名）
 */
const DEVICE_ID_KEY = 'felo_device_id';

/**
 * 生成 UUID v4
 */
const generateUUID = (): string => {
  // 使用 crypto API 生成 UUID（现代浏览器支持）
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // 降级方案：手动生成 UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * 获取或生成 device_id
 * 以 .felo.me 域 cookie 为准（官网/账号中心等子域共享同一设备标识，供促销设备防线使用），
 * localStorage 仅作旧值迁移来源与本地冗余
 */
export const getDeviceId = (): string => {
  try {
    let deviceId = Cookies.get(DEVICE_ID_KEY);
    if (!deviceId) {
      // 老用户迁移：沿用 localStorage 里已有的设备号，避免设备身份突变
      deviceId = localStorage.getItem(DEVICE_ID_KEY) || generateUUID();
      Cookies.set(DEVICE_ID_KEY, deviceId, {
        expires: 365,
        domain: import.meta.env.VITE_DOMAIN,
      });
    }
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    } catch {
      // localStorage 不可用不影响主流程
    }
    return deviceId;
  } catch (error) {
    console.error('[LogReporter] Failed to get/set device_id:', error);
    // 如果 cookie/localStorage 均不可用，返回临时 UUID
    return generateUUID();
  }
};

/**
 * 获取浏览器信息
 */
export const getBrowserInfo = (): {
  type: string;
  version: string;
} => {
  const ua = navigator.userAgent;
  let browserType = 'Unknown';
  let browserVersion = 'Unknown';

  // 检测浏览器类型和版本
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browserType = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browserType = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (ua.includes('Firefox')) {
    browserType = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (ua.includes('Edg')) {
    browserType = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    if (match) browserVersion = match[1];
  }

  return { type: browserType, version: browserVersion };
};

/**
 * 获取操作系统类型
 */
export const getOSType = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

/**
 * 获取时区
 */
export const getTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('[LogReporter] Failed to get timezone:', error);
    return 'UTC';
  }
};

/**
 * 组装日志数据
 */
export const buildLogData = (
  eventType: string,
  userId: string,
  additionalData?: Record<string, unknown>,
  triggerType: 'Manual' | 'Auto' = 'Manual',
): LogReportData => {
  const browserInfo = getBrowserInfo();
  const deviceId = getDeviceId();

  // 组装扩展信息
  const extData: LogEventExt = {
    current_url: window.location.href,
    source: 'MEMBER_CENTER',
    event_type: eventType,
    trigger_type: triggerType,
    ...(additionalData && { Data: additionalData }),
  };

  const logData: LogReportData = {
    browser_language: navigator.language || 'en-US',
    browser_type: browserInfo.type,
    browser_version: browserInfo.version,
    device_id: deviceId,
    event_time: Date.now(),
    ext: JSON.stringify(extData),
    os_type: getOSType(),
    report_application: 'subtitleExt',
    report_application_branch: import.meta.env.MODE === 'production' ? 'production' : 'development',
    report_application_version: '1.0.0', // 可以从 package.json 读取
    timezone: getTimezone(),
    user_id: userId,
  };

  return logData;
};

/**
 * 上报自定义日志
 *
 * @param eventType - 事件类型
 * @param userId - 用户 ID（可选）
 * @param additionalData - 额外数据（可选）
 * @param triggerType - 触发类型（可选，默认为 'Manual'）
 */
export const reportCustomLog = async (
  eventType: string,
  userId?: string,
  additionalData?: Record<string, unknown>,
  triggerType: 'Manual' | 'Auto' = 'Manual',
): Promise<void> => {
  try {
    const logData = buildLogData(eventType, userId ?? '', additionalData, triggerType);

    // 异步上报，不阻塞主流程
    reportLog(logData)
      .then(() => {
        if (import.meta.env.DEV) {
          console.log(
            '%c[自定义日志] ✅ 日志已上报',
            'color: #9c27b0; font-weight: bold;',
            '\n事件类型:', eventType,
            '\n用户 ID:', userId || '(anonymous)',
            '\n日志数据:', logData,
          );
        }
      })
      .catch((error) => {
        // 日志上报失败不影响业务流程,降级为 warn 避免刷红误导(服务端 503 等)
        console.warn('[自定义日志] 上报失败:', error);
      });
  } catch (error) {
    // 捕获所有错误，确保不影响业务
    console.error('[自定义日志] 组装数据失败:', error);
  }
};
